import React, { useState, useRef, useEffect } from "react";
import { useAlbumComments } from "../../hooks";
import { CreateAlbumCommentDto, UpdateAlbumCommentDto, AlbumComment } from "../../types/album";
import { useUser } from "../../hooks";
import CommentItem from "./CommentItem";
import LoadingSpinner from "./LoadingSpinner";
import DeleteCommentModal from "./DeleteCommentModal";
import OverlayRoot from "./OverlayRoot";

interface CommentsBottomSheetProps {
  ownerId: string;
  albumId: string;
  albumName: string;
  isOpen: boolean;
  onClose: () => void;
  canComment?: boolean;
}

const CommentsBottomSheet: React.FC<CommentsBottomSheetProps> = ({
  ownerId,
  albumId,
  albumName,
  isOpen,
  onClose,
  canComment = true,
}) => {
  const [newComment, setNewComment] = useState("");
  const [commentToDelete, setCommentToDelete] = useState<AlbumComment | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomSheetRef = useRef<HTMLDivElement>(null);

  const { user } = useUser();
  const {
    comments,
    hasComments,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    createComment,
    updateComment,
    deleteComment,
    refreshComments,
    clearError,
  } = useAlbumComments(ownerId, albumId);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [newComment]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      const commentData: CreateAlbumCommentDto = {
        content: newComment.trim(),
      };

      await createComment(commentData);
      setNewComment("");

    } catch (error) {
      console.error("Failed to create comment:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const handleUpdateComment = async (commentId: string, updateData: UpdateAlbumCommentDto) => {
    await updateComment(commentId, updateData);
  };

  const handleDeleteClick = (comment: AlbumComment) => {
    setCommentToDelete(comment);
  };

  const handleConfirmDelete = async () => {
    if (commentToDelete) {
      try {
        await deleteComment(commentToDelete.id);
        setCommentToDelete(null);
      } catch (error) {
        console.error("Failed to delete comment:", error);
      }
    }
  };

  const handleCancelDelete = () => {
    setCommentToDelete(null);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <OverlayRoot data-section="comments-bottom-sheet-root" className="fixed inset-0 z-[100] overscroll-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleBackdropClick}
      />

      {/* Bottom Sheet */}
      <div
        ref={bottomSheetRef}
        className={`absolute bottom-0 left-0 right-0 jp-sheet rounded-t-3xl shadow-2xl transform transition-all duration-500 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        } h-[85vh] max-h-[85vh] flex flex-col`}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden rounded-t-3xl">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-[rgba(var(--primary-green-rgb),0.2)] rounded-full blur-2xl" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/20 rounded-full blur-2xl" />
        </div>

        {/* Handle Bar */}
        <div className="relative flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-12 h-1.5 bg-white/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Comments</h2>
              <p className="text-gray-300 text-sm">{albumName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300"
              title="Close comments"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Area - takes remaining space */}
        <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Error State */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-500/10 backdrop-blur-xl border border-red-400/20 rounded-2xl flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-red-100 font-medium">Failed to load comments</p>
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => refreshComments()}
                    className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg text-sm transition-all duration-300"
                  >
                    Retry
                  </button>
                  <button
                    onClick={clearError}
                    className="px-3 py-1 bg-white/10 hover:bg-white/20 text-gray-300 rounded-lg text-sm transition-all duration-300"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Comments List - scrollable area that takes remaining space */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 py-4 min-h-0" style={{ scrollbarWidth: 'thin' }}>
            {isLoading && !hasComments ? (
              <div className="flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-16 h-16 bg-gradient-to-br from-[rgba(var(--primary-green-rgb),0.2)] to-[rgba(var(--primary-blue-rgb),0.2)] backdrop-blur-xl border border-white/20 rounded-2xl flex items-center justify-center mb-4">
                  <LoadingSpinner />
                </div>
                <p className="text-gray-400">Loading comments...</p>
              </div>
            ) : hasComments ? (
              <div className="space-y-4">
                {comments.map((comment, index) => (
                  <div
                    key={comment.id}
                    className="opacity-0 animate-slideIn"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <CommentItem
                      comment={comment}
                      isOwner={user?.id === comment.userId}
                      isUpdating={isUpdating}
                      isDeleting={isDeleting}
                      onUpdate={handleUpdateComment}
                      onDeleteClick={handleDeleteClick}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[200px]">
                <div className="w-20 h-20 bg-gradient-to-br from-[rgba(var(--primary-green-rgb),0.2)] to-[rgba(var(--primary-blue-rgb),0.2)] backdrop-blur-xl border border-white/20 rounded-3xl flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No comments yet</h3>
                <p className="text-gray-400 text-center">Be the first to share your thoughts on this album!</p>
              </div>
            )}
          </div>

          {/* Comment Form - conditionally visible at bottom */}
          {canComment ? (
            <div className="flex-shrink-0 p-6 border-t border-white/10 bg-black/20 backdrop-blur-xl">
              <div className="flex gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0 w-10 h-10 jp-gradient-brand rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {user?.id?.substring(0, 2).toUpperCase() || "ME"}
                  </span>
                </div>

                {/* Input Area */}
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add a comment... (Enter to send, Shift+Enter for new line)"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)] focus:border-[rgba(var(--primary-blue-rgb),0.55)] transition-all duration-300 resize-none min-h-[48px] max-h-[120px]"
                    disabled={isCreating}
                    rows={1}
                    maxLength={500}
                  />

                  {/* Character counter */}
                  <div className="absolute bottom-2 right-3 text-xs text-gray-400">
                    {newComment.length}/500
                  </div>
                </div>

                {/* Send Button */}
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || isCreating || newComment.length > 500}
                  className="flex-shrink-0 w-12 h-12 jp-fill-green disabled:bg-gray-600 rounded-2xl flex items-center justify-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 disabled:transform-none"
                >
                  {isCreating ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-shrink-0 p-6 border-t border-white/10 bg-black/20 backdrop-blur-xl">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-2xl">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-gray-300 text-sm">Sign in to add comments</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Comment Modal */}
      <DeleteCommentModal
        isOpen={!!commentToDelete}
        comment={commentToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </OverlayRoot>
  );
};

export default CommentsBottomSheet;